/**
 * Pending-invite token stash (multi-tenancy Phase F, bug #119).
 *
 * An invitee who clicks an emailed accept link while signed OUT hits
 * /accept-invite (RequireAuth) → bounced to /login → in OIDC mode the SPA
 * navigates the WINDOW to Zitadel's callbackUrl, does the PKCE exchange on
 * /auth/callback, and hard-lands on /app. That round-trip discards both the
 * router `from` location AND the `?token=` query string — so the token never
 * reaches the accept endpoint, and a brand-new (zero-org) invitee is bounced
 * by RequireOrg into the NEW-ORG onboarding flow instead of joining the
 * inviting org. (Reported as: "the invite link takes me to create a new
 * organisation instead of joining.")
 *
 * Fix: persist the token in sessionStorage the moment /accept-invite mounts
 * (before RequireAuth can redirect), so it survives the cross-origin sign-in
 * round-trip within the same tab. RequireOrg then re-routes a zero-org user
 * who has a stashed token back to /accept-invite to redeem it, rather than to
 * /onboarding. The actual join is still done server-side by the accept
 * endpoint against the token — this module only carries the token across the
 * redirect; it never grants membership or chooses an org itself.
 *
 * sessionStorage (not localStorage) is deliberate: the token is single-use and
 * short-lived, and we don't want a stale token lingering across browser
 * sessions or leaking into other tabs.
 */

const KEY = "divineiot:pending-invite";

/** Persist an invite token to redeem after the auth round-trip. No-op for an
 *  empty token or when storage is unavailable (private mode / quota). */
export function stashInviteToken(token: string): void {
  if (!token) return;
  try {
    window.sessionStorage.setItem(KEY, token);
  } catch {
    /* private mode / quota — best effort */
  }
}

/** Read the pending invite token without consuming it (RequireOrg uses this to
 *  decide whether to route a zero-org user to /accept-invite). */
export function peekInviteToken(): string | null {
  try {
    return window.sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}

/** Clear the pending invite token (called once it's been redeemed or after a
 *  terminal error, so a bad token can't trap the user in a redirect loop). */
export function clearInviteToken(): void {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
